import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, Clock, MousePointerClick } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import { fetchNode } from './api';
import UsageNodeList from './UsageNodeList';
import UsageNodeCharts from './UsageNodeCharts';
import UsageUserDetail from './UsageUserDetail';

const RANGES = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
  { key: '12mo', label: '12 months' },
  { key: 'all', label: 'All time' },
];

function fmtHours(ms) {
  const h = (ms || 0) / 3_600_000;
  return h >= 1 ? `${h.toFixed(1)}h` : `${Math.round(h * 60)}m`;
}

function StatCard({ icon: Icon, label, value, tone, dark }) {
  const tones = {
    blue: dark ? 'text-blue-300 bg-blue-500/10' : 'text-blue-600 bg-blue-50',
    orange: dark ? 'text-orange-300 bg-orange-500/10' : 'text-orange-600 bg-orange-50',
    green: dark ? 'text-emerald-300 bg-emerald-500/10' : 'text-emerald-600 bg-emerald-50',
  };
  return (
    <div className={`flex items-center gap-3 rounded-xl border p-4 ${dark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-white'} shadow-sm`}>
      <span className={`flex items-center justify-center w-10 h-10 rounded-lg ${tones[tone]}`}>
        <Icon size={20} />
      </span>
      <div>
        <div className={`text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</div>
        <div className="text-xl font-bold">{value}</div>
      </div>
    </div>
  );
}

export default function UsagePanel() {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [range, setRange] = useState('30d');
  // breadcrumb stack of drilled-into nodes; [] means "server default for my role"
  const [stack, setStack] = useState([]);
  const current = stack[stack.length - 1] || null;
  const isUser = current?.type === 'user';

  const { data, isLoading, error } = useQuery({
    queryKey: ['mgmt-usage-node', current?.type, current?.id, range],
    queryFn: () => fetchNode({ node: current, range }),
    enabled: !isUser,
  });

  const drill = (child) => setStack((s) => [...s, child]);
  const jumpTo = (idx) => setStack((s) => s.slice(0, idx + 1));
  const resetRoot = () => setStack([]);

  const rootName = (!stack.length && data?.node?.name) ? data.node.name : 'Overview';
  const crumbs = [{ name: rootName }, ...stack.map(n => ({ name: n.name || n.id }))];
  const summary = data?.summary || {};

  return (
    <div className="space-y-5">
      {/* Header: breadcrumb + range */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <nav className="flex items-center gap-1.5 text-sm flex-wrap min-w-0">
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-1.5 min-w-0">
              {i > 0 && <span className="text-gray-400">/</span>}
              <button
                onClick={i === crumbs.length - 1 ? undefined : () => (i === 0 ? resetRoot() : jumpTo(i - 1))}
                disabled={i === crumbs.length - 1}
                aria-current={i === crumbs.length - 1 ? 'page' : undefined}
                className={`truncate ${i === crumbs.length - 1
                  ? 'font-semibold cursor-default'
                  : 'text-blue-600 hover:underline'}`}
              >
                {c.name}
              </button>
            </span>
          ))}
        </nav>
        <div className={`inline-flex rounded-lg border p-0.5 ${dark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-gray-100'}`}>
          {RANGES.map(r => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                range === r.key
                  ? `${dark ? 'bg-gray-700 text-white' : 'bg-white text-blue-600 shadow-sm'}`
                  : `${dark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {isUser ? (
        <UsageUserDetail userId={current.id} name={current.name} range={range} theme={theme} />
      ) : isLoading ? (
        <div className="py-16 text-center text-gray-500">Loading…</div>
      ) : error ? (
        <div className="py-16 text-center text-red-500">Failed to load usage.</div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatCard icon={Users} label="Active users" value={summary.active_users ?? 0} tone="blue" dark={dark} />
            <StatCard icon={Clock} label="Total time" value={fmtHours(summary.total_duration_ms)} tone="orange" dark={dark} />
            <StatCard icon={MousePointerClick} label="Visits" value={summary.visits ?? 0} tone="green" dark={dark} />
          </div>

          {/* Charts */}
          <UsageNodeCharts compartments={data.compartments} trend={data.trend} theme={theme} />

          {/* Drill-down list */}
          {data.children?.length > 0 && (
            <div>
              <h3 className={`text-sm font-semibold mb-2 ${dark ? 'text-gray-300' : 'text-gray-600'}`}>Breakdown</h3>
              <UsageNodeList children={data.children} onDrill={drill} theme={theme} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
